import React, { useState } from "react";
import useBreakpoint from "../../../../utils/useBreakpoint";
import { LandTourDetail } from "../../../../interfaces/land-tour.interface";
import GroundTourModal from "./GroundTourModal";

const ICONS = "/iceholidays-assets/images/icons";

// The mobile card shows this many rows before "View all" takes over (Figma 10506:202075).
const MOBILE_VISIBLE_ITEMS = 5;

interface Item {
  icon: string;
  label: string;
}

interface Props {
  landTour: LandTourDetail;
}

const DESKTOP_COLUMNS = 3;

export const splitIntoColumns = <T,>(items: T[], columnCount = DESKTOP_COLUMNS): T[][] => {
  const base = Math.floor(items.length / columnCount);
  const extra = items.length % columnCount;
  let start = 0;

  return Array.from({ length: columnCount }, (_, index) => {
    const size = base + (index < extra ? 1 : 0);
    const column = items.slice(start, start + size);
    start += size;
    return column;
  });
};

const focLabel = (setting: number) => (setting > 1 ? `${setting - 1} + 1 Half Twin FOC` : "1 Half Twin FOC");

function IncludedRow({ item }: { item: Item }) {
  return (
    <div className="ground-tour-show-v2__included-item">
      {/* Masked with the brand red rather than shipping recolored asset copies. */}
      <span
        className="ground-tour-show-v2__included-icon"
        style={{
          WebkitMaskImage: `url(${ICONS}/icon-included-${item.icon}.png)`,
          maskImage: `url(${ICONS}/icon-included-${item.icon}.png)`,
        }}
        aria-hidden="true"
      />
      <span>{item.label}</span>
    </div>
  );
}

export default function WhatsIncluded({ landTour }: Props) {
  const isMobile = useBreakpoint();
  const [showAll, setShowAll] = useState(false);

  // Reading order - the Figma's grouping when every item is present.
  const groups: Item[][] = [
    [
      landTour.guideLanguages ? { icon: "globe", label: `Tour Guide: ${landTour.guideLanguages}` } : null,
      landTour.incHalfBoardMeals ? { icon: "meal", label: "Half Board Meal(s) Included" } : null,
      landTour.incTransportation ? { icon: "transport", label: "Transportation Included" } : null,
      landTour.tipping ? { icon: "cash", label: `Tipping ${landTour.tipping}` } : null,
    ].filter(Boolean) as Item[],
    [
      landTour.freeOfCharge ? { icon: "foc", label: focLabel(landTour.freeOfCharge) } : null,
      landTour.incHotel ? { icon: "hotel", label: "Hotel(s) Included" } : null,
      landTour.incFullBoardMeals ? { icon: "meal", label: "Full Board Meal(s) Included" } : null,
      landTour.incShoppingStop ? { icon: "shopping", label: "Shopping Stop Included" } : null,
    ].filter(Boolean) as Item[],
    [
      landTour.incFlight ? { icon: "flight", label: "Flight(s) Included" } : null,
      landTour.incEntranceTicket ? { icon: "ticket", label: "Entrance Ticket(s) Included" } : null,
      landTour.incInstantConfirmation ? { icon: "flash", label: "Instant Confirmation" } : null,
    ].filter(Boolean) as Item[],
  ];

  const items = groups.flat();
  if (!items.length) return null;

  const decoration = (
    <img
      className="ground-tour-show-v2__included-decoration"
      src={`${ICONS}/included-card-decoration.png`}
      alt=""
      aria-hidden="true"
    />
  );

  if (isMobile) {
    const hasMore = items.length > MOBILE_VISIBLE_ITEMS;

    return (
      <section className="ground-tour-show-v2__included">
        {decoration}

        <div className="ground-tour-show-v2__included-head">
          <h2 className="ground-tour-show-v2__included-title">What's Included</h2>

          {hasMore && (
            <button type="button" className="ground-tour-show-v2__included-viewall" onClick={() => setShowAll(true)}>
              View all
            </button>
          )}
        </div>

        <div className="ground-tour-show-v2__included-list">
          {items.slice(0, MOBILE_VISIBLE_ITEMS).map((item, index) => (
            <IncludedRow item={item} key={index} />
          ))}
        </div>

        <GroundTourModal open={showAll} onClose={() => setShowAll(false)} title="What's Included" isMobile>
          <div className="ground-tour-show-v2__included-list ground-tour-show-v2__included-list--modal">
            {items.map((item, index) => (
              <IncludedRow item={item} key={index} />
            ))}
          </div>
        </GroundTourModal>
      </section>
    );
  }

  return (
    <section className="ground-tour-show-v2__included">
      {decoration}

      <h2 className="ground-tour-show-v2__included-title">What's Included</h2>

      <div className="ground-tour-show-v2__included-grid">
        {splitIntoColumns(items).map((column, columnIndex) => (
          <div className="ground-tour-show-v2__included-column" key={columnIndex}>
            {column.map((item, itemIndex) => (
              <IncludedRow item={item} key={itemIndex} />
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}
